import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Store, store } from '@app/store/store';
import App from './App';
import './i18n';
import 'config/config';
import reportWebVitals from './reportWebVitals';
// Service worker disabled - uncomment below to enable PWA features
// import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { interceptor, initializeAxiosClient } from '@app/api/http.api';
import { loadConfig } from './services/configService';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(weekday);
dayjs.extend(utc);

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

loadConfig()
  .then(() => {
    initializeAxiosClient();
    interceptor(Store);

    root.render(
      <React.StrictMode>
        <Provider store={store}>
          <App />
        </Provider>
      </React.StrictMode>,
    );
  })
  .catch((error) => {
    console.error('Failed to initialize app:', error);

    root.render(
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Failed to load configuration</h1>
        <p>Please refresh the page</p>
      </div>,
    );
  });

// Service worker disabled - removes PWA features but avoids caching/redirect issues
// To enable: uncomment the import above and the code below
// if (process.env.NODE_ENV === 'production') {
//   serviceWorkerRegistration.register({
//     onUpdate: (registration) => {
//       const waitingServiceWorker = registration.waiting;
//       if (waitingServiceWorker) {
//         waitingServiceWorker.addEventListener('statechange', (event) => {
//           if ((event.target as ServiceWorker).state === 'activated') window.location.reload();
//         });
//         waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
//       }
//     },
//   });
// }

// Proactively unregister any existing service workers to disable old ones on all clients
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister().then((success) => {
        if (success) {
          console.log('Service worker unregistered:', registration.scope);
        }
      });
    });
  });

  // Also clear all caches
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName).then((success) => {
          if (success) {
            console.log('Cache deleted:', cacheName);
          }
        });
      });
    });
  }

  // Listen for messages from kill-switch service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SERVICE_WORKER_DISABLED') {
      console.log(event.data.message);
      // Optionally reload the page after a short delay
      // setTimeout(() => window.location.reload(), 1000);
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
